import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { useSyncRuns, useSyncStatus, useTriggerSync } from '../../hooks/useSyncRuns';
import { DataTable, type Column } from '../../components/DataTable';
import { useToast } from '../../components/Toast';
import { ApiError, apiClient } from '../../lib/api';
import { fmtDateTime, fmtRelative, fmtUsd } from '../../lib/format';
import type { IngestResultDto, QueryOptions, SourceSyncStatusDto, SyncRunDto } from '../../lib/types';

/** Sources shown as cards, whether or not they have run yet, and the slug POSTed to trigger them. */
const SOURCES: { source: string; label: string; slug: string | null; job: string | null; how: string }[] = [
  { source: 'ClaudeEnterprise', label: 'Claude (Enterprise Analytics)', slug: 'claude-code', job: 'SyncClaudeCode', how: 'Daily at 06:00 Central via the Functions timer; re-pulls the current month, plus the prior month for its first 5 days.' },
  { source: 'Codex', label: 'OpenAI Codex (leaderboard CSV)', slug: null, job: null, how: 'Posted by the scheduled Cowork job to POST /api/ingest/codex for a calendar month (see docs/codex-ingest.md). Upload here as a fallback; the latest export for a month replaces it.' },
  { source: 'ClaudePlatform', label: 'Claude API keys (Admin API)', slug: 'claude-api', job: 'SyncClaudeApi', how: 'Daily with the Claude sync. Exact cost per workspace from the Admin cost report; per-key estimates from usage tokens × the rate table, scaled to the workspace total. Needs the Admin API key.' }
];

export function Sync() {
  const isAdmin = useIsAdmin();
  if (!isAdmin) {
    return (
      <div className="bg-white rounded-lg shadow border border-slate-200 p-6">
        <h1 className="text-xl font-bold text-atg-navy">Forbidden</h1>
        <p className="text-slate-600 mt-2">You need the <code>Admin</code> role to view this page.</p>
      </div>
    );
  }
  return <SyncInner />;
}

function SyncInner() {
  const toast = useToast();
  const status = useSyncStatus();
  const trigger = useTriggerSync();
  const [options, setOptions] = useState<QueryOptions>({ page: 1, pageSize: 25 });
  const runs = useSyncRuns(options);

  const inFlight = new Set(status.data?.inFlight ?? []);
  const known = new Set(status.data?.knownJobs ?? []);

  async function run(slug: string, label: string) {
    try {
      const result = await trigger.mutateAsync(slug);
      if (result.accepted.length > 0) toast.success(`${label} sync queued.`);
      for (const r of result.rejected) toast.warning(`${r.job}: ${r.reason}`);
    } catch (e) {
      const body = (e as ApiError).body as { error?: string } | undefined;
      toast.error(body?.error ?? 'Could not start the sync.');
    }
  }

  const columns: Column<SyncRunDto>[] = [
    { header: 'Started', render: r => <span className="whitespace-nowrap">{fmtDateTime(r.startedAt)}</span> },
    { header: 'Source', render: r => sourceLabel(r.source) },
    { header: 'Status', render: r => <StatusPill status={r.status} /> },
    { header: 'Window', render: r => <span className="whitespace-nowrap tabular-nums">{r.windowStart} → {r.windowEnd}</span> },
    { header: 'Rows', render: r => <span className="tabular-nums">{r.rowsUpserted.toLocaleString()}{r.rowsSkipped > 0 && <span className="text-slate-400"> (+{r.rowsSkipped} skipped)</span>}</span>, cellClass: 'text-right', headerClass: 'text-right' },
    { header: 'By', render: r => <span className="text-slate-600">{r.triggeredBy}</span> },
    { header: 'Notes', render: r => <span className={`text-xs ${r.error ? 'text-red-600' : 'text-slate-600'}`}>{r.error ?? r.notes ?? ''}</span> }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-atg-navy">Data sync</h1>
        <p className="text-slate-600 mt-1 text-sm">Where each feed stands and a way to pull it now. Every run is recorded below.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {SOURCES.map(s => {
          const latest = status.data?.sources.find(x => x.source === s.source);
          const running = !!s.job && inFlight.has(s.job);
          const available = !!s.job && known.has(s.job) && !!s.slug;
          return (
            <div key={s.source} className="bg-white rounded-lg shadow border border-slate-200 p-4 flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-slate-800">{s.label}</h2>
                <span className="ml-auto"><StatusPill status={running ? 'Running' : latest?.status ?? 'Never'} /></span>
              </div>
              <LatestSummary latest={latest} />
              <p className="text-xs text-slate-500">{s.how}</p>
              {s.slug && (
                <button
                  onClick={() => run(s.slug!, s.label)}
                  disabled={!available || running || trigger.isPending}
                  className="mt-auto self-start rounded-md bg-atg-navy text-white px-3 py-1.5 text-sm font-medium hover:bg-atg-blue disabled:opacity-50"
                  title={available ? 'Queue a sync now' : 'Not available yet'}
                >
                  {running ? 'Syncing…' : 'Sync now'}
                </button>
              )}
              {s.source === 'Codex' && <CodexUpload />}
            </div>
          );
        })}
      </div>

      <DataTable<SyncRunDto>
        data={runs.data}
        isLoading={runs.isLoading}
        error={runs.error}
        options={options}
        onOptionsChange={setOptions}
        columns={columns}
        rowKey={r => r.id}
        searchable
        searchPlaceholder="Filter by source, status or who…"
        emptyState="No sync runs yet."
      />
    </div>
  );
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** Admin fallback for the Cowork job: pick the calendar month the export covers and upload it. */
function CodexUpload() {
  const toast = useToast();
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [month, setMonth] = useState(currentMonth());
  const [busy, setBusy] = useState(false);
  const [last, setLast] = useState<IngestResultDto | null>(null);

  async function upload(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.warning('Choose the leaderboard CSV first.'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('month', month);
    setBusy(true);
    try {
      const result = await apiClient.postForm<IngestResultDto>('/api/admin/ingest/codex', fd);
      setLast(result);
      toast.success(`Codex ${result.month}: ${result.developers} developers, ${fmtUsd(result.totalUsd)}.`);
      if (fileRef.current) fileRef.current.value = '';
      await Promise.all([qc.invalidateQueries({ queryKey: ['admin', 'sync'] }), qc.invalidateQueries({ queryKey: ['me'] })]);
    } catch (err) {
      const body = (err as ApiError).body as { error?: string } | undefined;
      toast.error(body?.error ?? 'Upload failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={upload} className="mt-auto border-t border-slate-100 pt-3 space-y-2">
      <div className="text-xs font-medium text-slate-700">Upload a leaderboard export</div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs text-slate-600">
          Month{' '}
          <input type="month" value={month} onChange={e => setMonth(e.target.value)} required className="rounded-md border border-slate-300 px-2 py-1 text-xs" />
        </label>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="text-xs" />
      </div>
      <button type="submit" disabled={busy} className="rounded-md border border-atg-navy text-atg-navy px-3 py-1.5 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">
        {busy ? 'Uploading…' : 'Upload'}
      </button>
      {last && (
        <ul className="text-xs text-slate-500 list-disc pl-4">
          {last.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}
    </form>
  );
}

function LatestSummary({ latest }: { latest: SourceSyncStatusDto | undefined }) {
  if (!latest) return <p className="text-xs text-slate-500">Has not run yet.</p>;
  return (
    <div className="text-xs text-slate-600 space-y-0.5">
      <div>Last run {fmtRelative(latest.finishedAt)} ({fmtDateTime(latest.finishedAt)})</div>
      {latest.windowStart && <div className="tabular-nums">Window {latest.windowStart} → {latest.windowEnd}</div>}
      {latest.dataRefreshedAt && <div>Vendor data refreshed {fmtDateTime(latest.dataRefreshedAt)}</div>}
      {latest.error && <div className="text-red-600">{latest.error}</div>}
    </div>
  );
}

function StatusPill({ status }: { status: string }) {
  const cls =
    status === 'Succeeded' ? 'bg-emerald-100 text-emerald-700'
    : status === 'Failed' ? 'bg-red-100 text-red-700'
    : status === 'Running' ? 'bg-amber-100 text-amber-800'
    : 'bg-slate-100 text-slate-600';
  return <span className={`text-xs px-2 py-1 rounded ${cls}`}>{status}</span>;
}

function sourceLabel(source: string): string {
  return SOURCES.find(s => s.source === source)?.label.split(' (')[0] ?? source;
}
